import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Chip,
  Card,
  Divider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTreeOutlined as WorkflowIcon,
  PersonOutline as PersonIcon,
  GroupOutlined as GroupIcon,
  AdminPanelSettingsOutlined as RoleIcon
} from '@mui/icons-material';
import {
  Workflow,
  WorkflowType,
  WorkflowStatus,
  ApprovalStep,
  ApprovalStepType,
  CreateWorkflowRequest,
  UpdateWorkflowRequest
} from '@/types/workflowTypes';
import { AttioTextField, AttioSelect } from '@/components/AttioForm';

interface WorkflowDialogProps {
  open: boolean;
  onClose: () => void;
  workflow?: Workflow | null;
  onSave: (
    workflow: CreateWorkflowRequest | UpdateWorkflowRequest
  ) => Promise<void>;
}

export const WorkflowDialog: React.FC<WorkflowDialogProps> = ({
  open,
  onClose,
  workflow,
  onSave
}) => {
  const theme = useTheme();
  const isEditing = !!workflow;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: WorkflowType.ApprovalChain,
    status: WorkflowStatus.Draft,
    allowParallelApproval: false,
    requireSequentialApproval: true,
    autoApproveAfterTimeout: false
  });

  const [steps, setSteps] = useState<ApprovalStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when editing
  useEffect(() => {
    if (workflow) {
      setFormData({
        name: workflow.name,
        description: workflow.description || '',
        type: workflow.type,
        status: workflow.status,
        allowParallelApproval: workflow.allowParallelApproval || false,
        requireSequentialApproval: workflow.requireSequentialApproval || true,
        autoApproveAfterTimeout: workflow.autoApproveAfterTimeout || false
      });
      setSteps(workflow.steps);
    } else {
      // Reset for new workflow
      setFormData({
        name: '',
        description: '',
        type: WorkflowType.ApprovalChain,
        status: WorkflowStatus.Draft,
        allowParallelApproval: false,
        requireSequentialApproval: true,
        autoApproveAfterTimeout: false
      });
      setSteps([]);
    }
    setErrors({});
  }, [workflow, open]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const addStep = () => {
    const newStep: ApprovalStep = {
      id: `temp-${Date.now()}-${Math.random()}`,
      order: steps.length + 1,
      name: `Step ${steps.length + 1}`,
      description: '',
      type: ApprovalStepType.UserRole,
      requiredRoles: ['Manager'],
      timeoutHours: 24,
      isOptional: false
    };
    setSteps((prev) => [...prev, newStep]);
  };

  const updateStep = (index: number, field: string, value: any) => {
    setSteps((prev) =>
      prev.map((step, i) => (i === index ? { ...step, [field]: value } : step))
    );
  };

  const removeStep = (index: number) => {
    setSteps((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, order: i + 1 }))
    );
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Workflow name is required';
    }

    if (steps.length === 0) {
      newErrors.steps = 'At least one approval step is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const workflowData = {
        ...formData,
        steps: steps.map((step, index) => ({ ...step, order: index + 1 })),
        triggerEvents: ['session_requires_approval'] // Default trigger
      };

      if (isEditing) {
        await onSave({
          id: workflow!.id,
          ...workflowData
        } as UpdateWorkflowRequest);
      } else {
        // For new workflows, omit the id field from steps
        const createData = {
          ...workflowData,
          steps: workflowData.steps.map(({ id, ...step }) => step) as Omit<
            ApprovalStep,
            'id'
          >[]
        };
        await onSave(createData as CreateWorkflowRequest);
      }

      onClose();
    } catch (error) {
      console.error('Failed to save workflow:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStepTypeIcon = (type: ApprovalStepType) => {
    switch (type) {
      case ApprovalStepType.UserRole:
        return <RoleIcon fontSize="small" />;
      case ApprovalStepType.SpecificUser:
        return <PersonIcon fontSize="small" />;
      case ApprovalStepType.AnyFromGroup:
      case ApprovalStepType.AllFromGroup:
        return <GroupIcon fontSize="small" />;
      default:
        return <WorkflowIcon fontSize="small" />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: '12px', 
          maxHeight: '90vh',
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 32px rgba(0, 0, 0, 0.6)' 
            : '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography 
          sx={{ 
            fontSize: '18px',
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          {isEditing ? 'Edit Workflow' : 'Create Workflow'}
        </Typography>
        <IconButton 
          onClick={onClose} 
          disabled={loading}
          sx={{
            borderRadius: '6px',
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          {/* Basic Information */}
          <Box>
            <Typography 
              sx={{ 
                fontSize: '11px',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 2.5,
                pt: 1.5
              }}
            >
              Basic Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <AttioTextField
                label="Workflow Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                placeholder="e.g., Session Approval Workflow"
              />

              <AttioTextField
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange('description', e.target.value)
                }
                placeholder="Describe what this workflow is used for..."
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: isEditing ? '1fr 1fr' : '1fr' }, gap: 2.5 }}>
                <AttioSelect
                  label="Workflow Type"
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  required
                >
                  <MenuItem value={WorkflowType.ApprovalChain}>
                    Approval Chain
                  </MenuItem>
                  <MenuItem value={WorkflowType.ReviewChain}>
                    Review Chain
                  </MenuItem>
                  <MenuItem value={WorkflowType.NotificationChain}>
                    Notification Chain
                  </MenuItem>
                </AttioSelect>

                {isEditing && (
                  <AttioSelect
                    label="Status"
                    value={formData.status}
                    onChange={(e) =>
                      handleInputChange('status', e.target.value)
                    }
                  >
                    <MenuItem value={WorkflowStatus.Draft}>Draft</MenuItem>
                    <MenuItem value={WorkflowStatus.Active}>Active</MenuItem>
                    <MenuItem value={WorkflowStatus.Inactive}>
                      Inactive
                    </MenuItem>
                  </AttioSelect>
                )}
              </Box>
            </Box>
          </Box>

          {/* Approval Steps */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2
              }}
            >
              <Box>
                <Typography 
                  sx={{ 
                    fontSize: '11px',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    pt: 1.5
                  }}
                >
                  Approval Steps
                </Typography>
                {errors.steps && (
                  <Typography 
                    sx={{ 
                      fontSize: '12px',
                      color: 'error.main',
                      mt: 0.5
                    }}
                  >
                    {errors.steps}
                  </Typography>
                )}
              </Box>
              <Button
                startIcon={<AddIcon />}
                onClick={addStep}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '13px',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                    borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                  }
                }}
              >
                Add Step
              </Button>
            </Box>

            {steps.length === 0 ? (
              <Alert 
                severity="info"
                sx={{
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)'
                }}
              >
                Add approval steps to define who can approve items in this workflow.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {steps.map((step, index) => (
                  <Card 
                    key={index}
                    sx={{
                      borderRadius: '8px',
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: 'none',
                      backgroundColor: theme.palette.background.paper
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2
                        }}
                      >
                        <Box
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5,
                            flexShrink: 0
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(59, 130, 246, 0.15)' 
                                : 'rgba(59, 130, 246, 0.1)',
                              color: theme.palette.primary.main,
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            {step.order}
                          </Box>
                          <Box
                            sx={{
                              color: theme.palette.text.secondary
                            }}
                          >
                            {getStepTypeIcon(step.type)}
                          </Box>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 180px' }, gap: 2.5, mb: 2 }}>
                            <AttioTextField
                              label="Step Name"
                              value={step.name}
                              onChange={(e) =>
                                updateStep(index, 'name', e.target.value)
                              }
                              placeholder="e.g., Manager Review"
                            />

                            <AttioSelect
                              label="Approval Type"
                              value={step.type}
                              onChange={(e) =>
                                updateStep(index, 'type', e.target.value)
                              }
                            >
                              <MenuItem value={ApprovalStepType.UserRole}>
                                By Role
                              </MenuItem>
                              <MenuItem value={ApprovalStepType.SpecificUser}>
                                Specific User
                              </MenuItem>
                              <MenuItem value={ApprovalStepType.AnyFromGroup}>
                                Any from Group
                              </MenuItem>
                              <MenuItem value={ApprovalStepType.AllFromGroup}>
                                All from Group
                              </MenuItem>
                            </AttioSelect>
                          </Box>

                          <AttioTextField
                            label="Description"
                            value={step.description || ''}
                            onChange={(e) =>
                              updateStep(index, 'description', e.target.value)
                            }
                            placeholder="Manager must review and approve session"
                          />

                          {/* Role chips below description (only for UserRole type) */}
                          {step.type === ApprovalStepType.UserRole && (
                            <Box
                              sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexWrap: 'wrap',
                                mt: 2
                              }}
                            >
                              {['Admin', 'Manager', 'User'].map((role) => {
                                const isSelected = step.requiredRoles?.includes(role);
                                return (
                                  <Chip
                                    key={role}
                                    label={role}
                                    onClick={() => {
                                      const currentRoles = step.requiredRoles || [];
                                      const newRoles = currentRoles.includes(role)
                                        ? currentRoles.filter((r) => r !== role)
                                        : [...currentRoles, role];
                                      updateStep(index, 'requiredRoles', newRoles);
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      height: '32px',
                                      fontSize: '13px',
                                      fontWeight: 500,
                                      borderRadius: '6px',
                                      backgroundColor: isSelected
                                        ? (theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.12)')
                                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'),
                                      color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                                      border: isSelected 
                                        ? `1px solid ${theme.palette.primary.main}` 
                                        : `1px solid ${theme.palette.divider}`,
                                      '&:hover': {
                                        backgroundColor: isSelected
                                          ? (theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.25)' : 'rgba(59, 130, 246, 0.18)')
                                          : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb'),
                                        borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider
                                      }
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        </Box>

                        <IconButton
                          onClick={() => removeStep(index)}
                          size="small"
                          sx={{
                            borderRadius: '6px',
                            color: theme.palette.text.secondary,
                            flexShrink: 0,
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
                              color: 'error.main'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            )}
          </Box>

          {/* Workflow Settings */}
          <Accordion
            sx={{
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              '&:before': {
                display: 'none'
              },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2.5,
                py: 1.5,
                '&.Mui-expanded': {
                  minHeight: 48
                },
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0'
                }
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
              >
                Advanced Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowParallelApproval}
                      onChange={(e) =>
                        handleInputChange(
                          'allowParallelApproval',
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Allow parallel approval
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        Multiple approvers can approve simultaneously
                      </Typography>
                    </Box>
                  }
                  labelPlacement="start"
                  sx={{ 
                    alignItems: 'flex-start', 
                    mx: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    ml: 0,
                    mr: 0,
                    width: '100%'
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requireSequentialApproval}
                      onChange={(e) =>
                        handleInputChange(
                          'requireSequentialApproval',
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Require sequential approval
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        Steps must be completed in order
                      </Typography>
                    </Box>
                  }
                  labelPlacement="start"
                  sx={{ 
                    alignItems: 'flex-start', 
                    mx: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    ml: 0,
                    mr: 0,
                    width: '100%'
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.autoApproveAfterTimeout}
                      onChange={(e) =>
                        handleInputChange(
                          'autoApproveAfterTimeout',
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Auto-approve after timeout
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        Automatically approve when timeout is reached
                      </Typography>
                    </Box>
                  }
                  labelPlacement="start"
                  sx={{ 
                    alignItems: 'flex-start', 
                    mx: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    ml: 0,
                    mr: 0,
                    width: '100%'
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1.5
        }}
      >
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            px: 2.5,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            px: 3,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          {loading
            ? 'Saving...'
            : isEditing
            ? 'Update Workflow'
            : 'Create Workflow'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
